import ApiImage from "@/components/ApiImage";
import WriteButton from "@/components/WriteButton";
import { useEffect, useMemo, useState, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/pos-api";
import { rupiah, imgUrl, stockLabel } from "@/lib/format";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Search, AlertTriangle, PackagePlus, Upload, Image as ImageIcon, Loader2, FileDown, Eye } from "lucide-react";
import MoneyInput from "@/components/MoneyInput";
import { productMatchesSearch } from "@/lib/product-codes.mjs";

const empty = {
  name: "", barcode: "", category_id: "",
  price_tiers: [{ name: "Eceran", min_qty: 1, price: 0 }],
  units: [{ name: "", pieces: 1, price: 0, barcode: "" }],
  variants: [{ name: "", barcode: "" }],
  cost: 0, stock: 0, min_stock: 5, image: "", note: "",
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const updRef = useRef(null);
  const [updatingPrices, setUpdatingPrices] = useState(false);
  const [histFor, setHistFor] = useState(null);
  const [hist, setHist] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [p, c] = await Promise.all([api.get("/products"), api.get("/categories")]);
      setProducts(p.data);
      setCategories(c.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/products/template", { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data); link.download = "template_produk.xlsx"; link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openHist = async (p) => {
    setHistFor(p); setHist(null);
    try { const { data } = await api.get(`/products/${p.id}/purchases`); setHist(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openPurchase = (h) => {
    setHistFor(null);
    navigate("/purchases", { state: { openPurchaseId: h.id } });
  };

  const downloadExport = async () => {
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (catFilter !== "all") params.category_id = catFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/products/export", { params, responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data); link.download = "daftar_produk.xlsx"; link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.errors?.length) toast.warning(`${data.errors.length} baris dilewati (${data.errors[0]})`);
      toast.success(`Impor selesai: ${data.created} produk baru`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setImporting(false); }
  };

  const [deletingAll, setDeletingAll] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((prev) => (filtered.length && filtered.every((p) => prev.has(p.id)) ? new Set() : new Set(filtered.map((p) => p.id))));

  const removeSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Hapus ${ids.length} produk terpilih? Tindakan ini tidak bisa dibatalkan.`)) return;
    if (!window.confirm("Yakin? Tekan OK sekali lagi untuk konfirmasi.")) return;
    setDeletingAll(true);
    try {
      const { data } = await api.post("/products/bulk-delete", { ids });
      toast.success(`${data.deleted} produk dihapus`);
      setSelected(new Set()); setSelectMode(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeletingAll(false); }
  };

  const handleUpdatePrices = async (e) => {
    const file = e.target.files?.[0];
    if (updRef.current) updRef.current.value = "";
    if (!file) return;
    setUpdatingPrices(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/products/update-prices", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.errors?.length) toast.warning(`${data.errors.length} produk dilewati (${data.errors[0]})`);
      toast.success(`Update harga selesai: ${data.updated} produk diperbarui`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUpdatingPrices(false); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, barcode: p.barcode || "", category_id: p.category_id || "",
      price_tiers: (p.price_tiers || []).map((t) => ({ name: t.name, min_qty: t.min_qty ?? 1, price: t.price ?? 0 })),
      units: (p.units || []).map((u) => ({ name: u.name, pieces: u.pieces ?? 1, price: u.price ?? 0, barcode: u.barcode || "" })),
      variants: (p.variants || []).map((v) => ({ name: v.name, barcode: v.barcode || "" })),
      cost: p.cost || 0, stock: p.stock || 0, min_stock: p.min_stock || 5, image: p.image || "", note: p.note || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama produk wajib diisi");
    if (!editing && !form.category_id) return toast.error("Kategori wajib dipilih untuk produk baru");
    if (form.units.some((u) => (u.barcode || "").trim() && (!u.name.trim() || !(Number(u.pieces) > 0)))) {
      return toast.error("Isi nama satuan dan jumlah isi lebih dari 0 untuk SKU satuan");
    }
    if (form.variants.some((v) => (v.barcode || "").trim() && !v.name.trim())) {
      return toast.error("Isi nama varian untuk SKU varian");
    }
    const tiers = form.price_tiers.filter((t) => t.name.trim());
    setSaving(true);
    const payload = {
      ...form,
      category_id: form.category_id || null,
      note: (form.note || "").trim() || null,
      price_tiers: tiers.map((t) => ({
        name: t.name.trim(),
        min_qty: Number(t.min_qty || 1),
        price: Number(t.price || 0),
      })),
      units: form.units.filter((u) => u.name.trim() && Number(u.pieces) > 0).map((u) => ({
        name: u.name.trim(),
        pieces: Number(u.pieces || 1),
        price: Number(u.price || 0),
        barcode: (u.barcode || "").trim() || null,
      })),
      variants: form.variants.filter((v) => v.name.trim()).map((v) => ({
        name: v.name.trim(),
        barcode: (v.barcode || "").trim() || null,
      })),
      base_unit: "Pcs",
      cost: Number(form.cost || 0),
      stock: Number(form.stock || 0),
      min_stock: Number(form.min_stock || 0),
    };
    if (editing) delete payload.stock;
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produk diperbarui" : "Produk ditambahkan");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Hapus produk "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Produk dihapus");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "-";
  const filtered = useMemo(() => products.filter((p) => {
    if (!productMatchesSearch(p, search)) return false;
    if (catFilter !== "all") {
      if (catFilter === "none" ? !!p.category_id : p.category_id !== catFilter) return false;
    }
    if (statusFilter !== "all") {
      const stock = p.stock || 0, mn = p.min_stock || 0;
      const noPrice = !(p.price_tiers || []).some((t) => Number(t.price) > 0);
      if (statusFilter === "no_price" && !noPrice) return false;
      if (statusFilter === "habis" && stock > 0) return false;
      if (statusFilter === "minus" && stock >= 0) return false;
      if (statusFilter === "low" && !(stock > 0 && stock <= mn)) return false;
    }
    return true;
  }), [products, search, catFilter, statusFilter]);
  const filterActive = catFilter !== "all" || statusFilter !== "all" || !!search.trim();

  const setTier = (i, key, val) =>
    setForm((f) => ({ ...f, price_tiers: f.price_tiers.map((t, x) => (x === i ? { ...t, [key]: val } : t)) }));
  const addTier = () =>
    setForm((f) => ({ ...f, price_tiers: [...f.price_tiers, { name: "", min_qty: 1, price: 0 }] }));
  const removeTier = (i) =>
    setForm((f) => ({ ...f, price_tiers: f.price_tiers.filter((_, x) => x !== i) }));

  const setUnit = (i, key, val) =>
    setForm((f) => ({ ...f, units: f.units.map((u, x) => (x === i ? { ...u, [key]: val } : u)) }));
  const addUnit = () =>
    setForm((f) => ({ ...f, units: [...f.units, { name: "", pieces: 1, price: 0, barcode: "" }] }));
  const removeUnit = (i) =>
    setForm((f) => ({ ...f, units: f.units.filter((_, x) => x !== i) }));

  const setVariant = (i, key, val) =>
    setForm((f) => ({ ...f, variants: f.variants.map((v, x) => (x === i ? { ...v, [key]: val } : v)) }));
  const addVariant = () =>
    setForm((f) => ({ ...f, variants: [...f.variants, { name: "", barcode: "" }] }));
  const removeVariant = (i) =>
    setForm((f) => ({ ...f, variants: f.variants.filter((_, x) => x !== i) }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Gambar terunggah");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <PageHeader title="Produk & Stok" subtitle={`${products.length} produk terdaftar`}>
        <div className="flex items-center gap-2">
          <input ref={fileRef} data-testid="product-import-file" type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
          <input ref={updRef} data-testid="product-updateprice-file" type="file" accept=".xlsx,.xls" onChange={handleUpdatePrices} className="hidden" />
          <Button data-testid="product-template-btn" variant="outline" onClick={downloadTemplate} className="border-ui-line bg-transparent hover:bg-ui-card gap-2">
            <FileDown className="h-4 w-4" /> <span className="hidden sm:inline">Template</span>
          </Button>
          <WriteButton data-testid="product-import-btn" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="border-brand/50 bg-transparent hover:bg-brand-soft text-brand-readable gap-2">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} <span className="hidden sm:inline">Import Excel</span>
          </WriteButton>
          <WriteButton data-testid="product-updateprice-btn" variant="outline" onClick={() => updRef.current?.click()} disabled={updatingPrices} className="border-warning/50 bg-transparent hover:bg-warning-soft text-warning-readable gap-2">
            {updatingPrices ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} <span className="hidden sm:inline">Update Harga</span>
          </WriteButton>
          <Button data-testid="product-export-btn" variant="outline" onClick={downloadExport} className="border-ui-line bg-transparent hover:bg-ui-card gap-2">
            <FileDown className="h-4 w-4 rotate-180" /> <span className="hidden sm:inline">Export</span>
          </Button>
          <WriteButton data-testid="product-select-btn" variant="outline" onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }} disabled={!products.length} className={`gap-2 bg-transparent ${selectMode ? "border-ui-line text-ui-text hover:bg-ui-card" : "border-danger/50 text-danger-readable hover:bg-danger-soft"}`}>
            <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">{selectMode ? "Batal Pilih" : "Hapus Produk"}</span>
          </WriteButton>
          <WriteButton data-testid="add-product-btn" onClick={openNew} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Tambah Produk</span>
          </WriteButton>
        </div>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
            <Input
              data-testid="product-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari produk / SKU / barcode…"
              className="pl-10 bg-ui-card border-ui-line"
            />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="product-cat-filter" className="w-[170px] bg-ui-card border-ui-line">
              <SelectValue placeholder="Kategori" />
            </SelectTrigger>
            <SelectContent className="bg-ui-card border-ui-line text-ui-text">
              <SelectItem value="all">Semua Kategori</SelectItem>
              <SelectItem value="none">Tanpa Kategori</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="product-status-filter" className="w-[180px] bg-ui-card border-ui-line">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-ui-card border-ui-line text-ui-text">
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="no_price">Belum Ada Harga</SelectItem>
              <SelectItem value="minus">Stok Minus</SelectItem>
              <SelectItem value="habis">Stok Habis (≤0)</SelectItem>
              <SelectItem value="low">Stok Menipis</SelectItem>
            </SelectContent>
          </Select>
          <span data-testid="product-filter-count" className="text-xs text-ui-muted">{filtered.length} produk</span>
          {filterActive && (
            <Button data-testid="product-filter-reset" variant="ghost" size="sm" onClick={() => { setSearch(""); setCatFilter("all"); setStatusFilter("all"); }} className="h-8 text-ui-muted hover:text-ui-text gap-1">
              <span className="text-xs">Reset filter</span>
            </Button>
          )}
        </div>

        <div className="rounded-xl border border-ui-line overflow-hidden">
          {selectMode && (
            <div className="flex flex-wrap items-center gap-3 border-b border-danger/40 bg-danger-soft p-3" data-testid="select-toolbar">
              <label className="flex items-center gap-2 text-sm text-ui-text cursor-pointer">
                <input type="checkbox" data-testid="select-all-checkbox" checked={filtered.length > 0 && filtered.every((p) => selected.has(p.id))} onChange={toggleAll} className="h-4 w-4 accent-brand" />
                Pilih Semua ({filtered.length})
              </label>
              <span className="text-sm text-ui-muted">Terpilih: <b className="text-ui-text">{selected.size}</b></span>
              <WriteButton data-testid="delete-selected-btn" onClick={removeSelected} disabled={!selected.size || deletingAll} variant="outline" className="ml-auto border-danger/50 bg-transparent text-danger-readable hover:bg-danger-soft gap-2">
                {deletingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Hapus Terpilih ({selected.size})
              </WriteButton>
            </div>
          )}
          <table className="w-full text-sm" data-testid="product-management-table">
            <thead className="bg-ui-card text-ui-muted text-xs uppercase tracking-wide">
              <tr>
                {selectMode && <th className="w-10 p-3"></th>}
                <th className="text-left p-3">Produk</th>
                <th className="text-left p-3 hidden md:table-cell">Kategori</th>
                <th className="text-left p-3">Harga Jual & Beli</th>
                <th className="text-center p-3">Stok</th>
                <th className="text-right p-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className={`border-t border-ui-line hover:bg-ui-soft ${selectMode && selected.has(p.id) ? "bg-brand-soft" : ""}`}>
                  {selectMode && (
                    <td className="p-3 align-top">
                      <input type="checkbox" data-testid={`select-product-${p.id}`} checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} className="h-4 w-4 accent-brand" />
                    </td>
                  )}
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {p.image && <ApiImage src={imgUrl(p.image)} alt="" className="h-8 w-8 rounded object-cover border border-ui-line" />}
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="flex items-center gap-1.5">
                          {p.barcode && <span className="text-[11px] text-ui-muted font-mono">{p.barcode}</span>}
                          <button data-testid={`peek-purchase-${p.id}`} onClick={() => openHist(p)} title="Intip histori pembelian" className="text-ui-muted hover:text-brand-readable">
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {p.last_supplier_name && (
                          <div data-testid={`last-supplier-${p.id}`} className="text-[11px] text-warning-readable/90" title="Supplier & tanggal pembelian terakhir">
                            ↳ beli terakhir: {p.last_supplier_name}{p.last_purchase_at ? ` · ${new Date(p.last_purchase_at).toLocaleDateString("id-ID")}` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 hidden md:table-cell text-ui-muted">{catName(p.category_id)}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1 items-center">
                      {(p.price_tiers || []).length === 0 && (p.units || []).length === 0 && (
                        <span className="text-[11px] text-ui-muted italic">harga jual belum diatur</span>
                      )}
                      {(p.price_tiers || []).map((t, ti) => (
                        <span key={`t${ti}`} data-testid={`price-tier-${p.id}-${ti}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-ui-card border border-ui-line">
                          <span className="text-ui-text font-medium">{t.name}{t.min_qty > 1 ? ` ≥${t.min_qty}` : ""}</span>
                          <span className="font-mono text-brand-readable font-semibold">{rupiah(t.price)}</span>
                          <span className="text-ui-muted">·</span>
                          <span className="font-mono text-rose-600 font-medium" title="Harga beli / modal per Pcs (otomatis dari Pembelian)">beli {rupiah(p.cost || 0)}</span>
                        </span>
                      ))}
                      {(p.units || []).map((u, ui) => (
                        <span key={`u${ui}`} data-testid={`price-unit-${p.id}-${ui}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-accent-soft border border-accent/30">
                          <span className="text-accent-readable font-medium">{u.name}</span>
                          <span className="font-mono text-brand-readable font-semibold">{rupiah(u.price)}</span>
                          <span className="text-ui-muted">·</span>
                          <span className="font-mono text-rose-600 font-medium" title={`Harga beli / modal ${u.name} = cost × ${u.pieces} pcs`}>beli {rupiah((p.cost || 0) * (u.pieces || 1))}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`inline-flex items-center gap-1 ${p.stock <= p.min_stock ? "text-warning-readable" : "text-ui-muted"}`}>
                      {p.stock <= p.min_stock && <AlertTriangle className="h-3.5 w-3.5" />}
                      <span className="text-xs">{stockLabel(p.stock, p.units, p.base_unit)}</span>
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <WriteButton plain data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-ui-muted-surface flex items-center justify-center">
                        <Pencil className="h-4 w-4 text-ui-muted" />
                      </WriteButton>
                      <WriteButton plain data-testid={`delete-product-${p.id}`} onClick={() => remove(p)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-danger-soft flex items-center justify-center">
                        <Trash2 className="h-4 w-4 text-danger-readable" />
                      </WriteButton>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={selectMode ? 6 : 5} className="p-10 text-center text-ui-muted">
                  <PackagePlus className="h-8 w-8 mx-auto mb-2" /> Belum ada produk
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!histFor} onOpenChange={(v) => !v && setHistFor(null)}>
        <DialogContent data-testid="peek-purchase-dialog" className="bg-ui-card border-ui-line text-ui-text max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Histori Pembelian · {histFor?.name}</DialogTitle>
            <DialogDescription className="text-ui-muted text-xs">Klik No. pembelian untuk membuka detailnya.</DialogDescription>
          </DialogHeader>
          {hist === null ? (
            <div className="py-8 text-center text-ui-muted text-sm">Memuat…</div>
          ) : hist.length === 0 ? (
            <div className="py-8 text-center text-ui-muted text-sm">Belum ada histori pembelian untuk produk ini</div>
          ) : (
            <>
              <div data-testid="peek-summary" className="flex items-center justify-between text-xs bg-ui-card border border-ui-line rounded-lg px-3 py-2">
                <span className="text-ui-muted">{hist.length}x pembelian</span>
                <span className="text-ui-muted font-mono">Total {hist.reduce((s, h) => s + Number(h.qty || 0) * Number(h.pieces || 1), 0)} pcs · {rupiah(hist.reduce((s, h) => s + Number(h.qty || 0) * Number(h.cost || 0), 0))}</span>
              </div>
              <div className="max-h-[50vh] overflow-y-auto space-y-1.5 mt-2" data-testid="peek-purchase-list">
                {hist.map((h, i) => {
                  const pcs = Number(h.pieces || 1);
                  const totalPcs = Number(h.qty || 0) * pcs;
                  const total = Number(h.qty || 0) * Number(h.cost || 0);
                  const perPcs = totalPcs > 0 ? total / totalPcs : 0;
                  return (
                  <div key={i} data-testid={`peek-purchase-item-${i}`} className="rounded-lg border border-ui-line bg-ui-soft px-3 py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <button data-testid={`peek-invoice-${i}`} onClick={() => openPurchase(h)} className="text-sm font-semibold text-brand-readable hover:underline font-mono">{h.invoice_no}</button>
                      <div className="text-[11px] text-ui-muted">{h.supplier_name || "-"} · {h.date || h.created_at}</div>
                    </div>
                    <div className="text-right shrink-0 font-mono">
                      <div className="text-sm text-ui-text">{h.qty} {h.unit}{pcs > 1 ? ` = ${totalPcs} pcs` : ""}</div>
                      <div className="text-[11px] text-brand-readable">total {rupiah(total)}</div>
                      <div data-testid={`peek-perpcs-${i}`} className="text-[11px] text-warning-readable">{rupiah(perPcs)} / pcs</div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="product-form-dialog" className="bg-ui-card border-ui-line text-ui-text w-[calc(100%-2rem)] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Produk" : "Tambah Produk"}</DialogTitle>
            <DialogDescription className="text-ui-muted text-xs">Atur detail produk, stok, dan tingkat harga per minimal qty.</DialogDescription>
          </DialogHeader>
          <div className="min-w-0 space-y-4">
            <Field label="Nama Produk">
              <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ui-card border-ui-line" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="SKU / Barcode Produk">
                <Input data-testid="form-barcode" aria-label="SKU / Barcode Produk" placeholder="Ketik atau scan SKU produk" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="bg-ui-card border-ui-line font-mono" />
              </Field>
              <Field label={editing ? "Kategori" : "Kategori *"}>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="form-category" className="bg-ui-card border-ui-line"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                  <SelectContent className="bg-ui-card border-ui-line text-ui-text">
                    {editing && <SelectItem value="none">Tanpa Kategori</SelectItem>}
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="rounded-xl border border-ui-line p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-ui-muted font-semibold">Satuan Produk (mis. BKS / SLP / Dus)</div>
                <WriteButton type="button" data-testid="add-unit-btn" onClick={addUnit} size="sm" variant="outline" className="h-8 border-ui-line bg-transparent hover:bg-ui-card gap-1 text-xs">
                  <Plus className="h-3 w-3" /> Satuan
                </WriteButton>
              </div>
              {form.units.length === 0 ? (
                <p className="text-[11px] text-ui-muted">Stok disimpan dalam Pcs. Tambahkan satuan seperti Dus (mis. isi 24) — menjual 1 Dus otomatis memotong 24 Pcs.</p>
              ) : (
                <div className="overflow-x-auto">
                <div className="grid min-w-[680px] grid-cols-[1fr_1.4fr_64px_1fr_1fr_36px] gap-2 items-center">
                  <Label className="text-[10px] text-ui-muted uppercase">Nama</Label>
                  <Label className="text-[10px] text-ui-muted uppercase">SKU / Barcode</Label>
                  <Label className="text-[10px] text-ui-muted uppercase text-center">Isi (Pcs)</Label>
                  <Label className="text-[10px] text-ui-muted uppercase text-right">Harga Jual</Label>
                  <Label className="text-[10px] text-ui-muted uppercase text-right">Modal</Label>
                  <span />
                  {form.units.map((u, i) => (
                    <Fragment key={i}>
                      <Input data-testid={`unit-name-${i}`} value={u.name} onChange={(e) => setUnit(i, "name", e.target.value)} placeholder="Dus" className="bg-ui-card border-ui-line h-10" />
                      <Input data-testid={`unit-barcode-${i}`} aria-label={`SKU / Barcode satuan ${u.name || i + 1}`} value={u.barcode || ""} onChange={(e) => setUnit(i, "barcode", e.target.value)} placeholder="SKU satuan (opsional)" className="bg-ui-card border-ui-line h-10 font-mono" />
                      <Input data-testid={`unit-pieces-${i}`} type="number" value={u.pieces} onChange={(e) => setUnit(i, "pieces", e.target.value)} className="bg-ui-card border-ui-line h-10 font-mono text-center" />
                      <MoneyInput data-testid={`unit-price-${i}`} value={u.price} onValueChange={(v) => setUnit(i, "price", v)} className="bg-ui-card border-ui-line h-10 font-mono text-right" />
                      <div data-testid={`unit-modal-${i}`} title="Harga beli/modal = harga beli Pcs × isi" className="h-10 flex items-center justify-end px-2 rounded-lg bg-ui-soft/70 border border-ui-line font-mono text-danger-readable text-sm">{rupiah(Number(form.cost || 0) * Number(u.pieces || 1))}</div>
                      <button type="button" data-testid={`unit-remove-${i}`} onClick={() => removeUnit(i)} className="h-10 w-9 rounded-lg bg-ui-card hover:bg-danger-soft flex items-center justify-center">
                        <Trash2 className="h-4 w-4 text-danger-readable" />
                      </button>
                    </Fragment>
                  ))}
                </div>
                </div>
              )}
              {!editing && <p className="text-[11px] text-ui-muted">Isi SKU masing-masing satuan. Baris boleh dikosongkan jika produk tidak memakai satuan tambahan.</p>}
            </div>
            <div className="rounded-xl border border-ui-line p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-ui-muted font-semibold">Tingkat Harga (opsional · per Min. Qty)</div>
                <WriteButton type="button" data-testid="add-tier-btn" onClick={addTier} size="sm" variant="outline" className="h-8 border-ui-line bg-transparent hover:bg-ui-card gap-1 text-xs">
                  <Plus className="h-3 w-3" /> Tingkat
                </WriteButton>
              </div>
              <div className="grid grid-cols-[1fr_56px_1fr_1fr_36px] gap-2 items-center">
                <Label className="text-[10px] text-ui-muted uppercase">Nama</Label>
                <Label className="text-[10px] text-ui-muted uppercase text-center">Min Qty</Label>
                <Label className="text-[10px] text-ui-muted uppercase text-right">Harga Jual</Label>
                <Label className="text-[10px] text-ui-muted uppercase text-right">Modal</Label>
                <span />
                {form.price_tiers.map((t, i) => (
                  <Fragment key={i}>
                    <Input data-testid={`tier-name-${i}`} value={t.name} onChange={(e) => setTier(i, "name", e.target.value)} placeholder="Eceran" className="bg-ui-card border-ui-line h-10" />
                    <Input data-testid={`tier-minqty-${i}`} type="number" value={t.min_qty} onChange={(e) => setTier(i, "min_qty", e.target.value)} className="bg-ui-card border-ui-line h-10 font-mono text-center" />
                    <MoneyInput data-testid={`tier-price-${i}`} value={t.price} onValueChange={(v) => setTier(i, "price", v)} className="bg-ui-card border-ui-line h-10 font-mono text-right" />
                    <div data-testid={`tier-modal-${i}`} title="Harga beli/modal per Pcs" className="h-10 flex items-center justify-end px-2 rounded-lg bg-ui-soft/70 border border-ui-line font-mono text-danger-readable text-sm">{rupiah(Number(form.cost || 0))}</div>
                    <button type="button" data-testid={`tier-remove-${i}`} onClick={() => removeTier(i)} className="h-10 w-9 rounded-lg bg-ui-card hover:bg-danger-soft flex items-center justify-center">
                      <Trash2 className="h-4 w-4 text-danger-readable" />
                    </button>
                  </Fragment>
                ))}
              </div>
              <p className="text-[11px] text-ui-muted">Harga grosir per satuan: beri Tingkat Harga <b className="text-ui-muted">nama SAMA dengan satuan</b> + Min Qty. Contoh: satuan <b className="text-ui-muted">RCG</b> Rp 9.000, tingkat harga <b className="text-ui-muted">RCG Min 5</b> Rp 8.500 → beli ≥5 otomatis Rp 8.500. Harga tetap bisa diubah manual di kasir.</p>
            </div>
            <div className="rounded-xl border border-ui-line p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-ui-muted font-semibold">Varian (mis. Warna/Ukuran/Rasa)</div>
                <WriteButton type="button" data-testid="add-variant-btn" onClick={addVariant} size="sm" variant="outline" className="h-8 border-ui-line bg-transparent hover:bg-ui-card gap-1 text-xs">
                  <Plus className="h-3 w-3" /> Varian
                </WriteButton>
              </div>
              {form.variants.length === 0 ? (
                <p className="text-[11px] text-ui-muted">Tambahkan varian seperti Merah, Pink, Biru. Stok tetap digabung (varian hanya label). Di kasir muncul pilihan varian saat produk dipilih.</p>
              ) : (
                <div className="grid grid-cols-[1fr_1fr_36px] gap-2 items-center">
                  <Label className="text-[10px] text-ui-muted uppercase">Nama Varian</Label>
                  <Label className="text-[10px] text-ui-muted uppercase">SKU / Barcode (opsional)</Label>
                  <span />
                  {form.variants.map((v, i) => (
                    <Fragment key={i}>
                      <Input data-testid={`variant-name-${i}`} value={v.name} onChange={(e) => setVariant(i, "name", e.target.value)} placeholder="Merah" className="bg-ui-card border-ui-line h-10" />
                      <Input data-testid={`variant-barcode-${i}`} aria-label={`SKU / Barcode varian ${v.name || i + 1}`} value={v.barcode} onChange={(e) => setVariant(i, "barcode", e.target.value)} placeholder="SKU varian (opsional)" className="bg-ui-card border-ui-line h-10 font-mono" />
                      <button type="button" data-testid={`variant-remove-${i}`} onClick={() => removeVariant(i)} className="h-10 w-9 rounded-lg bg-ui-card hover:bg-danger-soft flex items-center justify-center">
                        <Trash2 className="h-4 w-4 text-danger-readable" />
                      </button>
                    </Fragment>
                  ))}
                </div>
              )}
              {!editing && <p className="text-[11px] text-ui-muted">Isi nama dan SKU setiap varian, atau kosongkan baris jika produk tidak memiliki varian.</p>}
            </div>
            <Field label="Harga Beli / Modal (per Pcs)">
              <MoneyInput data-testid="form-cost" value={form.cost} onValueChange={(v) => setForm({ ...form, cost: v })} className="bg-ui-card border-ui-line font-mono text-right" />
              <p className="text-[11px] text-ui-muted">Otomatis diperbarui dari harga Pembelian terakhir. Isi manual untuk modal awal.</p>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={editing ? "Stok (ubah via Opname/Pembelian/Transfer)" : "Stok Awal"}>
                <Input data-testid="form-stock" type="number" value={form.stock} disabled={!!editing} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="bg-ui-card border-ui-line font-mono text-right disabled:opacity-50 disabled:cursor-not-allowed" />
              </Field>
              <Field label="Stok Min">
                <Input data-testid="form-minstock" type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="bg-ui-card border-ui-line font-mono text-right" />
              </Field>
            </div>
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Catatan Produk (mis. keterangan, promo, letak rak)</Label>
              <textarea
                data-testid="form-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Tulis catatan untuk produk ini…"
                rows={2}
                className="w-full rounded-lg bg-ui-card border border-ui-line text-sm p-2.5 text-ui-text placeholder:text-ui-muted focus:outline-none focus:ring-1 focus:ring-brand/50 resize-none"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Gambar Produk (foto / JPG)</Label>
              <div className="flex items-center gap-3">
                <div className="h-20 w-20 rounded-lg bg-ui-soft border border-ui-line overflow-hidden flex items-center justify-center shrink-0">
                  {form.image ? (
                    <ApiImage src={imgUrl(form.image)} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-7 w-7 text-ui-muted" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label data-testid="upload-image-label" className="inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-ui-card border border-ui-line hover:border-brand cursor-pointer text-sm no-select">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? "Mengunggah…" : "Pilih Foto / File JPG"}
                    <input data-testid="form-image-file" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={handleUpload} className="hidden" disabled={uploading} />
                  </label>
                  {form.image && (
                    <button type="button" data-testid="remove-image-btn" onClick={() => setForm({ ...form, image: "" })} className="text-xs text-danger-readable hover:text-danger-readable text-left">Hapus gambar</button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-ui-line bg-transparent hover:bg-ui-card">Batal</Button>
            <WriteButton data-testid="save-product-btn" onClick={save} disabled={saving} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold">Simpan</WriteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-ui-muted text-xs">{label}</Label>
      {children}
    </div>
  );
}
